<template>
  <el-dialog
    :model-value="visible"
    :title="mode === 'create' ? '创建商品' : '修改商品'"
    width="720px"
    append-to-body
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <el-form ref="formRef" :model="formState" :rules="rules" label-width="96px">
      <el-form-item label="分类" prop="categoryId">
        <el-select v-model="formState.categoryId" placeholder="请选择分类" style="width: 100%">
          <el-option v-for="item in categoryOptions" :key="item.value" :label="item.label" :value="item.value" />
        </el-select>
      </el-form-item>

      <el-form-item label="名称" prop="name">
        <el-input v-model="formState.name" placeholder="请输入商品名称" />
      </el-form-item>

      <el-form-item label="描述" prop="description">
        <el-input v-model="formState.description" type="textarea" :rows="3" placeholder="请输入商品描述" />
      </el-form-item>

      <el-form-item label="图片" prop="image">
        <div class="upload-field">
          <div v-if="formState.image" class="image-preview">
            <el-image :src="getFullUrl(formState.image)" fit="cover" />
          </div>
          <el-upload
            :action="normalizedUploadUrl"
            :headers="uploadHeaders"
            name="files"
            :show-file-list="false"
            accept="image/*"
            :on-success="handleUploadSuccess"
          >
            <el-button type="primary" plain>上传图片</el-button>
          </el-upload>
        </div>
      </el-form-item>

      <el-row :gutter="12">
        <el-col :span="12">
          <el-form-item label="价格" prop="price">
            <el-input v-model="formState.price" placeholder="请输入价格" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="赠送积分" prop="giftPoints">
            <el-input-number v-model="formState.giftPoints" :min="0" :precision="0" style="width: 100%" />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="12">
        <el-col :span="12">
          <el-form-item label="库存" prop="stock">
            <el-input-number v-model="formState.stock" :min="0" :precision="0" style="width: 100%" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="排序" prop="sequence">
            <el-input-number v-model="formState.sequence" :min="0" :precision="0" style="width: 100%" />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="12">
        <el-col :span="12">
          <el-form-item label="上架状态">
            <el-switch v-model="formState.isOnSale" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="积分兑换">
            <el-switch v-model="formState.isPointsExchange" />
          </el-form-item>
        </el-col>
      </el-row>

      <el-form-item label="规格配置">
        <GoodsSpecEditor v-model="formState.specs" />
      </el-form-item>
    </el-form>

    <template #footer>
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" :loading="submitting" @click="handleSubmit">确定</el-button>
    </template>
  </el-dialog>
</template>

<script lang="ts" setup>
import { computed, reactive, ref, watch } from 'vue';
import { ElMessage, type FormInstance, type FormRules } from 'element-plus';
import GoodsSpecEditor from './goods-spec-editor.vue';

interface GoodsSpec {
  name: string;
  options: string[];
}

interface GoodsEditorFormState {
  id?: number;
  categoryId?: number;
  image?: string;
  name: string;
  description: string;
  price: string;
  giftPoints: number;
  stock: number;
  sequence: number;
  isOnSale: boolean;
  isPointsExchange: boolean;
  specs: GoodsSpec[];
}

const props = defineProps<{
  visible: boolean;
  mode: 'create' | 'edit';
  initialValue?: Partial<GoodsEditorFormState>;
  categoryOptions: { label: string; value: number }[];
  uploadUrl: string;
  uploadHeaders: Record<string, string>;
  baseUrl: string;
  submitting?: boolean;
}>();

// eslint-disable-next-line func-call-spacing
const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void;
  (e: 'submit', value: GoodsEditorFormState): void;
}>();

const formRef = ref<FormInstance>();

const createDefaultState = (): GoodsEditorFormState => ({
  id: undefined,
  categoryId: undefined,
  image: '',
  name: '',
  description: '',
  price: '',
  giftPoints: 0,
  stock: 0,
  sequence: 0,
  isOnSale: true,
  isPointsExchange: false,
  specs: [],
});

const formState = reactive<GoodsEditorFormState>(createDefaultState());

const rules: FormRules = {
  categoryId: [{ required: true, message: '请选择分类', trigger: 'change' }],
  name: [{ required: true, message: '请输入名称', trigger: 'blur' }],
  description: [{ required: true, message: '请输入描述', trigger: 'blur' }],
  image: [{ required: true, message: '请上传图片', trigger: 'change' }],
  price: [
    { required: true, message: '请输入价格', trigger: 'blur' },
    { pattern: /^\d+(?:\.\d{1,2})?$/u, message: '价格格式不正确，最多保留两位小数', trigger: 'blur' },
  ],
};

const sanitizeSpecs = (specs: GoodsSpec[]) => (specs || [])
  .map((item) => ({
    name: (item.name || '').trim(),
    options: (item.options || []).map((option) => (option || '').trim()).filter(Boolean),
  }))
  .filter((item) => item.name && item.options.length > 0)
  .map((item) => ({
    name: item.name,
    options: Array.from(new Set(item.options)),
  }));

const syncFormState = () => {
  const nextState = {
    ...createDefaultState(),
    ...(props.initialValue || {}),
    isPointsExchange: Boolean(props.initialValue?.isPointsExchange),
    specs: sanitizeSpecs((props.initialValue?.specs || []) as GoodsSpec[]),
  };
  Object.assign(formState, nextState);
};

watch(() => props.visible, (visible) => {
  if (visible) {
    syncFormState();
  }
}, { immediate: true });

const normalizedUploadUrl = computed(() => {
  if (!props.uploadUrl) {
    return props.baseUrl;
  }
  if (props.uploadUrl.startsWith('http')) {
    return props.uploadUrl;
  }
  if (props.uploadUrl.startsWith(`${props.baseUrl}/`) || props.uploadUrl === props.baseUrl) {
    return props.uploadUrl;
  }
  return `${props.baseUrl}${props.uploadUrl}`;
});

const getFullUrl = (url?: string) => {
  if (!url) {
    return '';
  }
  if (url.startsWith('http')) {
    return url;
  }
  return props.baseUrl + url;
};

const handleUploadSuccess = (response: any) => {
  const imageUrl = response?.data?.[0];
  if (imageUrl) {
    formState.image = imageUrl;
  }
};

const handleClose = () => {
  emit('update:visible', false);
};

const handleSubmit = async() => {
  await formRef.value?.validate();
  const specs = sanitizeSpecs(formState.specs);
  const hasInvalidSpec = (formState.specs || []).some((item) => {
    const groupName = (item.name || '').trim();
    const options = (item.options || []).map((option) => (option || '').trim()).filter(Boolean);
    return !groupName || options.length === 0;
  });
  if (hasInvalidSpec) {
    ElMessage.error('请完善规格组名称和至少一个规格值');
    return;
  }
  emit('submit', {
    ...formState,
    specs,
  });
};
</script>

<style scoped lang="scss">
.upload-field {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;

  .image-preview {
    width: 120px;
    height: 120px;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #dcdfe6;

    :deep(.el-image) {
      width: 100%;
      height: 100%;
    }
  }
}
</style>
